import {
  FormControl,
  FormErrorMessage,
  Flex,
  Spacer,
  InputGroup,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Stack,
  Button,
  Select,
  Heading,
  Input,
  InputRightElement,
  useDisclosure,
} from "@chakra-ui/react";

import React, { useEffect, useMemo, useState} from "react";
import { AddIcon } from "@chakra-ui/icons";

export default function NewChip(props) {
  const { setAlert, organizationsData } = props;

  const organizations = useMemo(() => organizationsData, [organizationsData]);
  const [departments, setDepartments] = useState([]);

  const authToken = localStorage.getItem('authToken');
  const userData = JSON.parse(localStorage.getItem('userData'));

  
  // Modal states and fields
  const { isOpen: isChipOpen, onOpen: onChipOpen, onClose: onChipClose } = useDisclosure();
  const [nRFId, setnRFId] = useState('');
  const [nRFIdInvalid, setnRFIdInvalid] = useState(false);
  const [chipId, setChipId] = useState('');
  const [chipIdInvalid, setChipIdInvalid] = useState(false);
  const [organization, setOrganization] = useState('');
  const [organizationInvalid, setOrganizationInvalid] = useState(false);
  const [department, setDepartment] = useState('');
  const [departmentInvalid, setDepartmentInvalid] = useState(false);

  // Existing chip identifiers used for client-side duplicate-check
  // (backend has a unique index on nrf_id — see issue #7).
  const [existingNrfIds, setExistingNrfIds] = useState(new Set());

  // Fetch existing chips when modal opens so we can validate inline.
  useEffect(() => {
    if (!isChipOpen) return;
    fetch('https://api.beehiveproject.no/chip', {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((r) => r.json())
      .then((chips) => {
        if (Array.isArray(chips)) {
          setExistingNrfIds(new Set(chips.map((c) => c.nrf_id).filter(Boolean)));
        }
      })
      .catch((err) => console.error('Failed to fetch chips for duplicate-check:', err));
  }, [isChipOpen, authToken]);

  // Derived: trimmed nrf_id duplicates an existing chip
  const trimmedNRFId = nRFId.trim();
  const nRFIdDuplicate = trimmedNRFId !== '' && existingNrfIds.has(trimmedNRFId);

  const createChip = () => {
    let inputs = [nRFId, chipId, organization, department]
    let inputInvalids = [setnRFIdInvalid, setChipIdInvalid, setOrganizationInvalid, setDepartmentInvalid]
    let abort = false
    for (var i = 0; i < inputs.length; i++)
      {
        if (inputs[i] === '')
          {
            inputInvalids[i](true)
            abort = true
          }
      }

    if (abort)
      return

    // Block creation if nrf_id is already in the database.
    if (nRFIdDuplicate) {
      setAlert([
        'error',
        'Duplikat',
        `nRF ID "${trimmedNRFId}" finnes allerede i databasen. Bruk en annen ID eller rediger den eksisterende chippen.`,
      ]);
      return;
    }

    onChipClose()
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({
        nrf_id: trimmedNRFId,
        chip_id: chipId,
        organization: organization._id.$oid,
        department: department._id.$oid,
      })
    };

    fetch('https://api.beehiveproject.no/chip', options)
      .then(response => {
        if (response.status === 200) {
          setAlert(['success', 'Suksess', `Ny chip har blitt opprettet i avdeling ${department.name} under organisasjonen ${organization.name}`])
          setnRFId('');
          setChipId('');
          setDepartment('');
          // Optimistically add to existing-ids so a re-attempt fails fast
          setExistingNrfIds((prev) => new Set(prev).add(trimmedNRFId));
        } else if (response.status === 409) {
          // Backend ideally returns 409 for duplicates
          setAlert(['error', 'Duplikat', `nRF ID "${trimmedNRFId}" finnes allerede i databasen.`])
        } else {
          // Current backend often returns 500 on DuplicateKeyError — assume
          // that's the likely cause and tell the user.
          setAlert(['error', 'Feil', `Kunne ikke opprette chip. nRF ID "${trimmedNRFId}" kan allerede være i bruk, eller serveren svarte med en feil (HTTP ${response.status}).`])
        }
      })
      .catch(error => {
        console.error(error);
        setAlert(['error', 'Feil', 'Nettverksfeil under oppretting av chip.'])
      });
  }

  // Fetch departments
  const fetchDepartments = async () => {
    if (!organization || !organization._id) return [];
    try {
      const response = await fetch(
        `https://api.beehiveproject.no/organization/${organization._id.$oid}/departments`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const departments = await response.json();
      setDepartments(departments);

      return departments;
    } catch (error) {
      console.error("Failed to fetch departments:", error);
      return [];
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, [organization]);

  return (

    <Flex>
      <Button onClick={onChipOpen} leftIcon={<AddIcon/>} colorScheme='brand'>Ny Chip</Button>
      <Modal isOpen={isChipOpen} onClose={onChipClose}>
        <ModalOverlay/>
        <ModalContent>
          <ModalHeader>Opprett Ny Chip</ModalHeader>
          <ModalCloseButton/>
          <ModalBody>
            <Stack spacing='4'>
              <Heading size='sm'>Chip ID</Heading>
              <FormControl isRequired isInvalid={chipIdInvalid}>
                <Input value={chipId} onChange={(event) => setChipId(event.target.value)} placeholder='Chip ID' />
                <FormErrorMessage>Chip ID er påkrevd</FormErrorMessage>
              </FormControl>

              <Heading size='sm'>nRF ID</Heading>
              <FormControl isRequired isInvalid={nRFIdInvalid || nRFIdDuplicate}>
                <Input value={nRFId} onChange={(event) => setnRFId(event.target.value)} placeholder='nRF ID' />
                <FormErrorMessage>
                  {nRFIdDuplicate
                    ? `nRF ID "${trimmedNRFId}" finnes allerede i databasen`
                    : 'nRF ID er påkrevd'}
                </FormErrorMessage>
              </FormControl>
                            
              <Heading size='sm'>Organisasjon</Heading>
              <FormControl isRequired isInvalid={organizationInvalid}>
                <Select
                  variant="outline"
                  defaultValue=""
                  onChange={(event) => {
                    const data = JSON.parse(event.target.value);
                    setOrganization(data);
                  }}
                >
                  <option value="" disabled hidden>
                    Velg en organisasjon
                  </option>
                  {organizations.map((org) => (<option value={JSON.stringify(org)} key={org._id.$oid}>{org.name}</option>))}
                </Select>
                <FormErrorMessage>Organisasjon er påkrevd</FormErrorMessage>
              </FormControl>

              <Heading size='sm'>Avdeling</Heading>
              <FormControl isRequired isInvalid={departmentInvalid}>
                <Select 
                  variant="outline"
                  defaultValue=""
                  onChange={(event) => {
                    let data = JSON.parse(event.target.value)
                    setDepartment(data)
                  }}
                >
                  <option value="" disabled hidden>
                    Velg en avdeling
                  </option>
                  {departments.map((dep) => <option value={JSON.stringify(dep)} key={dep._id.$oid}>{dep.icon} {dep.name}</option>)}
                </Select>
                <FormErrorMessage>Avdeling er påkrevd</FormErrorMessage>
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onChipClose} colorScheme='red'>Avbryt</Button>
            <Spacer/>
            <Button onClick={createChip} colorScheme='brand' isDisabled={nRFIdDuplicate}>Opprett</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  )
}